import { apiClient } from '../client';
import type { Metric, MetricEntry } from '@/api/scorecards.api';
import type { Headline } from './headlines.api';
import type { Issue } from './issues.api';
import type { Rock } from './rocks.api';
import type { Todo } from './todos.api';

/**
 * Client for `/api/eos/meetings/*`. Stays in lockstep with
 * `server/src/routes/eos/meetings.routes.ts` (Stream B Wave 2, ADR-023):
 * series CRUD + occurrence creation are gated on `run_meetings`; reads
 * are member-level; the runner endpoints answer with the typed error
 * codes mapped in `src/lib/eos/errors.ts`.
 */

export type MeetingStatus = 'scheduled' | 'in_progress' | 'concluded' | 'canceled';

export interface AgendaSegment {
  key: string;
  label: string;
  minutes: number;
}

/**
 * Frontend mirror of `server/src/modules/eos/meetings/agenda.ts` —
 * seeds the series agenda editor. The two MUST stay in lockstep.
 */
export const DEFAULT_L10_AGENDA: AgendaSegment[] = [
  { key: 'segue', label: 'Segue', minutes: 5 },
  { key: 'scorecard', label: 'Scorecard review', minutes: 5 },
  { key: 'rocks', label: 'Rock review', minutes: 5 },
  { key: 'headlines', label: 'Headlines', minutes: 5 },
  { key: 'todos', label: 'To-do review', minutes: 5 },
  { key: 'ids', label: 'IDS', minutes: 60 },
  { key: 'conclude', label: 'Conclude', minutes: 5 },
];

// ── Series ──────────────────────────────────────────────────────────────

export interface MeetingSeries {
  id: string;
  organizationId: string;
  name: string;
  /** Null = company/leadership team meeting. */
  departmentId: string | null;
  /** 0=Sunday .. 6=Saturday. */
  dayOfWeek: number;
  /** "HH:MM" 24h. */
  startTime: string;
  durationMinutes: number;
  /** Raw stored agenda — `[]` means "use the default L10 agenda". */
  agendaJson: unknown;
  isActive: boolean;
  createdByMembershipId: string | null;
  createdAt: string;
  updatedAt: string;
  /** Server-resolved agenda (falls back to the default when empty/invalid). */
  agenda: AgendaSegment[];
}

export async function listMeetingSeries(): Promise<MeetingSeries[]> {
  const res = await apiClient.get<{ data: MeetingSeries[] }>('/api/eos/meetings/series');
  return res.data.data;
}

export interface CreateSeriesRequest {
  name: string;
  departmentId?: string | null;
  dayOfWeek: number;
  /** "HH:MM" 24h. */
  startTime: string;
  durationMinutes?: number;
  /** Ordered segments; omit or `[]` = the default L10 agenda. */
  agendaJson?: AgendaSegment[];
}

export async function createMeetingSeries(body: CreateSeriesRequest): Promise<MeetingSeries> {
  const res = await apiClient.post<{ data: MeetingSeries }>('/api/eos/meetings/series', body);
  return res.data.data;
}

export interface UpdateSeriesRequest {
  name?: string;
  departmentId?: string | null;
  dayOfWeek?: number;
  startTime?: string;
  durationMinutes?: number;
  agendaJson?: AgendaSegment[];
}

export async function updateMeetingSeries(id: string, body: UpdateSeriesRequest): Promise<MeetingSeries> {
  const res = await apiClient.put<{ data: MeetingSeries }>(
    `/api/eos/meetings/series/${encodeURIComponent(id)}`,
    body,
  );
  return res.data.data;
}

/** Soft delete — sets `isActive: false`; past meetings keep their link. */
export async function deactivateMeetingSeries(id: string): Promise<void> {
  await apiClient.delete(`/api/eos/meetings/series/${encodeURIComponent(id)}`);
}

// ── Meetings ────────────────────────────────────────────────────────────

export interface Meeting {
  id: string;
  organizationId: string;
  seriesId: string | null;
  departmentId: string | null;
  scheduledFor: string;
  status: MeetingStatus;
  startedAt: string | null;
  concludedAt: string | null;
  facilitatorMembershipId: string | null;
  /** Live-runner cursor — null unless in progress. */
  currentSegment: string | null;
  segmentStartedAt: string | null;
  segmentLogJson: unknown;
  /** Computed at conclude from attendee ratings. */
  averageRating: number | null;
  summaryText: string | null;
  createdAt: string;
  updatedAt: string;
}

export type MeetingListItem = Meeting & { seriesName: string | null; attendeeCount: number };

export interface ListMeetingsQuery {
  seriesId?: string;
  status?: MeetingStatus;
  /** ISO datetime with offset. */
  from?: string;
  /** ISO datetime with offset. */
  to?: string;
}

export async function listMeetings(q: ListMeetingsQuery = {}): Promise<MeetingListItem[]> {
  const params = new URLSearchParams();
  if (q.seriesId) params.set('seriesId', q.seriesId);
  if (q.status) params.set('status', q.status);
  if (q.from) params.set('from', q.from);
  if (q.to) params.set('to', q.to);
  const query = params.toString() ? `?${params.toString()}` : '';
  const res = await apiClient.get<{ data: MeetingListItem[] }>(`/api/eos/meetings${query}`);
  return res.data.data;
}

export interface CreateMeetingRequest {
  seriesId?: string;
  departmentId?: string | null;
  /** ISO datetime with offset. */
  scheduledFor: string;
}

export async function createMeeting(body: CreateMeetingRequest): Promise<Meeting> {
  const res = await apiClient.post<{ data: Meeting }>('/api/eos/meetings', body);
  return res.data.data;
}

export interface SegmentLogEntry {
  segment: string;
  startedAt: string;
  endedAt?: string;
}

/** Attendee as the detail/workspace reads shape it (with display name). */
export interface MeetingAttendee {
  id: string;
  membershipId: string;
  present: boolean;
  rating: number | null;
  name: string;
}

export type MeetingDetail = Meeting & {
  seriesName: string | null;
  agenda: AgendaSegment[];
  segmentLog: SegmentLogEntry[];
  attendees: MeetingAttendee[];
};

export async function getMeeting(id: string): Promise<MeetingDetail> {
  const res = await apiClient.get<{ data: MeetingDetail }>(`/api/eos/meetings/${encodeURIComponent(id)}`);
  return res.data.data;
}

// ── Workspace (the runner's composite read) ─────────────────────────────

export type WorkspaceMeeting = Meeting & {
  seriesName: string | null;
  segmentLog: SegmentLogEntry[];
  attendees: MeetingAttendee[];
};

/** Scorecard metric with its last 4 entries (newest first), status included.
 *  Keeps the scalar `seatId` but drops the `seat` relation — the workspace
 *  service selects entries only (no seat include). */
export type WorkspaceMetric = Omit<Metric, 'ownerEmployee' | 'seat'> & { entries: MetricEntry[] };

export type WorkspaceTodo = Todo & {
  ownerMembership: { id: string; user: { firstName: string; lastName: string } } | null;
};

export interface MeetingWorkspace {
  meeting: WorkspaceMeeting;
  agenda: AgendaSegment[];
  teamScorecard: WorkspaceMetric[];
  rocks: Rock[];
  openTodos: WorkspaceTodo[];
  rankedIssues: Issue[];
  headlines: Headline[];
}

export async function getMeetingWorkspace(id: string): Promise<MeetingWorkspace> {
  const res = await apiClient.get<{ data: MeetingWorkspace }>(
    `/api/eos/meetings/${encodeURIComponent(id)}/workspace`,
  );
  return res.data.data;
}

// ── Runner ──────────────────────────────────────────────────────────────

/** scheduled → in_progress; the caller becomes the facilitator. */
export async function startMeeting(id: string): Promise<Meeting> {
  const res = await apiClient.post<{ data: Meeting }>(`/api/eos/meetings/${encodeURIComponent(id)}/start`);
  return res.data.data;
}

/** Facilitator-only. Omit `toKey` for "next segment"; pass it to jump. */
export async function advanceSegment(id: string, toKey?: string): Promise<Meeting> {
  const res = await apiClient.put<{ data: Meeting }>(
    `/api/eos/meetings/${encodeURIComponent(id)}/segment`,
    toKey !== undefined ? { toKey } : {},
  );
  return res.data.data;
}

export interface AttendanceEntry {
  membershipId: string;
  present: boolean;
}

/** Raw attendee row — the bulk-attendance/rating endpoints skip the name join. */
export interface AttendeeRow {
  id: string;
  meetingId: string;
  organizationId: string;
  membershipId: string;
  present: boolean;
  rating: number | null;
  createdAt: string;
  updatedAt: string;
}

/** Facilitator-only bulk upsert of the attendee sheet. */
export async function setAttendees(id: string, entries: AttendanceEntry[]): Promise<AttendeeRow[]> {
  const res = await apiClient.put<{ data: AttendeeRow[] }>(
    `/api/eos/meetings/${encodeURIComponent(id)}/attendees`,
    { entries },
  );
  return res.data.data;
}

/** Self-service — the caller must already be on the attendee sheet. */
export async function submitRating(id: string, rating: number): Promise<AttendeeRow> {
  const res = await apiClient.post<{ data: AttendeeRow }>(
    `/api/eos/meetings/${encodeURIComponent(id)}/rating`,
    { rating },
  );
  return res.data.data;
}

export interface MeetingRecap {
  ratings: { average: number | null; submittedCount: number; attendeeCount: number };
  /** The open meeting to-dos that were pushed a week + carry-counted. */
  todosCarried: Todo[];
  todosCreatedCount: number;
  issuesSolvedCount: number;
}

/** Facilitator-only. in_progress → concluded; returns the recap. */
export async function concludeMeeting(
  id: string,
  summaryText?: string,
): Promise<{ meeting: Meeting; recap: MeetingRecap }> {
  const res = await apiClient.post<{ data: { meeting: Meeting; recap: MeetingRecap } }>(
    `/api/eos/meetings/${encodeURIComponent(id)}/conclude`,
    summaryText !== undefined ? { summaryText } : {},
  );
  return res.data.data;
}
